import { useEffect, useRef, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import Modal from '../Modal';
import { FormErrorBanner } from '../FormErrorBanner';
import {
  hiringLinksAPI,
  type HiringLink,
  type HiringPeopleSearch,
  type HiringPersonOption,
  type HiringRaiseBlocking,
  type HiringRaiseBudget,
  type HiringRaiseInput,
  type HiringRaisePreview,
  type HiringRaiseReason,
  type HiringRaiseResult,
  type HiringRequest,
  type HiringRequestSent,
  type HiringStoreOption,
} from '../../services/api';
import {
  DELETED_WORDS,
  HIRING_CODES,
  NO_RAISE_NEEDS,
  RAISE_FORM,
  RAISE_SEGMENTS,
  RAISE_TICKS,
  RAISE_WORDS,
  REQUEST_WORDS,
  REVIEW_WORDS,
  TEAM_CODES,
  blockingRequestOf,
  budgetIsFull,
  buildRaiseBody,
  hiringCodeOf,
  hiringStatusOf,
  isHiringOff,
  isNoFreePosition,
  isRaiseOff,
  listIsBehindAfter,
  localiseRaiseMessage,
  makeRaiseRequestKey,
  nothingWasSentBy,
  openBlockingWords,
  openingStoreWords,
  openingToLinkInstead,
  personTagOf,
  raiseErrorText,
  raiseLinkMoved,
  raiseMissingLine,
  raiseNeedsAfter,
  raiseOutcomeHeading,
  raiseOutcomeKind,
  raiseReasonHover,
  raiseReasonLabel,
  raiseRefusalStep,
  reviewReplacesWords,
  storeAddressLine,
  teamMissingLine,
  teamNotAllowedOf,
  typedAddressMissing,
  type HiringMapPoint,
  type RaiseFormNeeds,
} from './hiringText';
import { ManageBudgetsLink } from './ManageBudgetsLink';
import { RaiseRequestStatus } from './RaiseRequestStatus';
import { HiringTeamSection, useHiringTeam } from './HiringTeamSection';
import { FormRow, Segmented } from './HiringForm';
import { BudgetSelect, CitySelect, PersonPicker, StorePicker, type PickedCity } from './HiringPickers';
import { HiringSelect } from './HiringSelect';
import { HiringAddressInput, type AddressPick } from './HiringAddressInput';
import { RaiseReview } from './RaiseReview';

/**
 * Raise hiring: ask Recruitment to CREATE an opening, at a store or with no store. One
 * window, three steps: the form, the review, the result.
 *
 * WHAT A WRONG CLICK COSTS. A create makes a real, live opening: Recruitment mails its
 * recruiters and can alert candidates, and ChannelPlex cannot take it back. So:
 *
 *   - Nothing is sent from the form. "Review" asks the server what WOULD be sent and
 *     what Recruitment will do, and the review step shows it.
 *   - The request key is made ONCE, when the review step opens, and goes with every
 *     repeat of that confirm. The server answers a repeat from the first request, so one
 *     review can never create two openings.
 *   - "Yes, raise hiring" is disabled from the first click until an answer, or a
 *     failure, is on screen.
 *   - Closing the window sends nothing, and opening it again starts at an empty form: it
 *     never sends by itself.
 *   - When no answer comes back, the form is not offered again. The only thing to press
 *     repeats the SAME request, with the same key.
 *   - A request Recruitment did not confirm never offers "Raise again".
 *
 * Every refusal is the server's own sentence, and every refusal code has one step here
 * (hiringText's raiseRefusalStep): the window closes, the form is shown again (with the
 * tick or the city picker the sentence asks for, or the team marked or read again), the
 * review is thrown away and made again under a NEW key, or the sentence is shown and the
 * only thing to press repeats the same request. Where Recruitment already has an unlinked
 * opening for it, "Link it instead" is offered beside the choice to raise one more.
 *
 * THE FORM (approved mock-up, 15 Oct 2026: a short form, labels on the left).
 *   - Two entries. From a store's row (`entry="store"`, `subject`): the store is one line at
 *     the top, with "Show address" (and "Edit store" where the owner has its window). From
 *     the Hiring dashboard (`entry="dashboard"`, no subject): the store is an optional
 *     picker, "No store" first; with no store the address is typed (a city of Recruitment's
 *     list and a PIN on one row, then the address). There is no role name: a hiring with no
 *     store is named after its budget by the server, and no budget default address is offered.
 *   - Why: two segmented buttons, "Replacing someone" and "One more person" ("New store" only
 *     as `fixedReason`, one read-only line).
 *   - Who (replacing): one searchable person picker. For a chosen store - the row's, or one
 *     picked by hand on the dashboard - ONLY that store's people (deployed there now, or left
 *     it in the last 30 days), searched within them when 2 letters are typed; the server
 *     refuses anyone else (15 Oct 2026, the product owner). With no store: the Leaving list
 *     when opened, the project's people when 2 letters are typed, and choosing a person fills
 *     their store ("Their store") - which does not narrow "Who". Choosing a person fills their
 *     own budget. A store picked by hand is never overwritten by the person picked, and a
 *     person picked before who is not of that store is dropped.
 *   - Budget: every active budget, Store-level ones tagged, "56 free of 147", Full greyed.
 *   - Team: recruiters and offer makers as chips, one select per round. In ask mode there is
 *     no team: one line says the person who confirms picks it.
 *   - The foot: one grey line saying what is still missing, Cancel, Review.
 * People replaced are employees, by name and code; recruitment staff by name. Names are
 * read fresh, shown, and never kept. No phone number is drawn.
 *
 * RAISED AGAIN FOR A DELETED OPENING (`replacingDeleted`, 21 Oct 2026). Opened by "Raise again"
 * on an opening deleted in Recruitment: once this raise has made an opening (created, or found
 * followed by hand), ChannelPlex stops following the deleted one in the same step - the same
 * call as its own "Stop following", which changes nothing in Recruitment - and the result says
 * so. If that fails the raise stands, and the result says "Stop following" is still needed
 * there. A raise Recruitment has not confirmed lets go of nothing until it is (here, while the
 * window is open); one that made nothing lets go of nothing. In ask mode nothing is let go of
 * either: the result says the deleted opening stays followed.
 *
 * ASK MODE (`mode="ask"`). For a person who may ask for hiring and may not raise it: the same
 * form, pickers, checks and review WITHOUT the team, and at the end "Send for confirmation".
 * NOTHING is sent to Recruitment from an ask-mode window: every call is one of the `request…`
 * calls, and "Link it instead" is not drawn. WHO MAY RAISE is decided before the window
 * opens (`canRaise`: "Raise & cancel hiring"); the owner passes `mode`, and it never changes.
 */

/** 'raise' sends to Recruitment at the end; 'ask' saves a hiring request for someone else to confirm. */
export type RaiseHiringMode = 'raise' | 'ask';

/** Where the window was opened: a store's row (the store is fixed) or the Hiring dashboard (the store is optional). */
export type RaiseHiringEntry = 'store' | 'dashboard';

export type RaiseHiringSubject =
  | {
      kind: 'store';
      storeId: string;
      storeName: string;
      storeCode: string;
      address: string;
      city: string;
      state: string;
      pincode: string;
      hasMapLocation: boolean;
      status: string;
      staffingStatus: string;
    }
  /** Before 15 Oct 2026, "a manager role": now the dashboard's window, the store optional. */
  | { kind: 'role' };

/** What "Link it instead" hands the owner: the budget, the opening, and the store when one is chosen. */
export interface RaiseLinkInsteadPick {
  budgetId: number;
  openingId: number;
  store?: Pick<HiringStoreOption, 'storeId' | 'storeName' | 'storeCode'> | null;
  /** @deprecated No role name is typed since 15 Oct 2026; never sent. */
  roleName?: string;
}

const PRIMARY = 'inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary-600 px-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50';
const SECONDARY = 'inline-flex h-9 items-center rounded-lg border border-gray-300 bg-white px-3.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const LINK = 'whitespace-nowrap text-[13px] font-medium text-primary-600 underline decoration-primary-200 underline-offset-2 hover:text-primary-700 disabled:opacity-50';
const INPUT = 'h-9 w-full min-w-0 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100';

const NO_LINKS: readonly HiringLink[] = [];

interface Reviewed {
  preview: HiringRaisePreview;
  /** Exactly what was reviewed. The confirm sends this same body. */
  body: HiringRaiseInput;
  /** Made when this review step opened; every repeat of its confirm carries it. */
  requestKey: string;
}

/** A store given by its row, in the shape of the pickers' stores. */
const storeOfSubject = (subject: Extract<RaiseHiringSubject, { kind: 'store' }>): HiringStoreOption => ({
  storeId: subject.storeId,
  storeName: subject.storeName,
  storeCode: subject.storeCode,
  city: subject.city,
  state: subject.state,
  address: subject.address,
  pincode: subject.pincode,
  hasMapPoint: subject.hasMapLocation,
  status: subject.status,
  staffingStatus: subject.staffingStatus,
  refused: null,
  refusedMessage: null,
});

/** A person's store, known by name and code only until the store search has said more. */
const storeOfPerson = (store: NonNullable<HiringPersonOption['store']>): HiringStoreOption => ({
  storeId: store.storeId,
  storeName: store.storeName,
  storeCode: store.storeCode,
  city: null,
  state: null,
  address: null,
  pincode: null,
  hasMapPoint: false,
  status: null,
  staffingStatus: null,
  refused: null,
  refusedMessage: null,
});

export function RaiseHiringModal({
  projectId, subject, entry, mode = 'raise', links = NO_LINKS, onClose, onChanged, onAsked, onOutOfDate, onHiringGone, onRaiseGone,
  openRequest, onLinkInstead, fixedReason, onEditStore, replacing, replacingDeleted,
}: {
  projectId: number;
  /** The store, from a store's row. Absent (or `{ kind: 'role' }`) from the Hiring dashboard. */
  subject?: RaiseHiringSubject;
  /** 'store': the store is fixed (`subject`). 'dashboard': the store is an optional picker. Follows `subject` when left out. */
  entry?: RaiseHiringEntry;
  /** 'ask' saves a hiring request for a person who may raise hiring to confirm; nothing is sent to Recruitment. */
  mode?: RaiseHiringMode;
  /** The store's links as the owner has them: a request already being raised is shown in place of a form. */
  links?: readonly HiringLink[];
  onClose: () => void;
  /** A request exists or has moved on: its link, as the server just answered it. */
  onChanged: (link: HiringLink) => void;
  /** Ask mode: a hiring request was saved, as the server just answered it. */
  onAsked?: (request: HiringRequest) => void;
  /** The owner's list may be missing a request: an answer was lost, or the window closed while one was out. */
  onOutOfDate?: () => void;
  /** Hiring was switched off for the project while the window was open. */
  onHiringGone: () => void;
  /** Raising was switched off, for the project or on the server, while the window was open. */
  onRaiseGone: () => void;
  /** For the request that is in the way under a budget: something that opens it, when the owner has it on show. */
  openRequest?: (linkId: string) => (() => void) | null;
  /**
   * Recruitment already has an unlinked opening for this store (or this address), and the
   * person would rather follow that one than raise another: the owner closes this window and
   * opens Link an opening on it, with the budget, the opening and the store (if any).
   * Without it, "Link it instead" is not drawn.
   */
  onLinkInstead?: (pick: RaiseLinkInsteadPick) => void;
  /**
   * The reason, already decided by where the window was opened: New store, for a store that
   * was just added. One read-only line in place of the two buttons. The ONLY way "New store"
   * is offered.
   */
  fixedReason?: HiringRaiseReason;
  /**
   * "Edit store" on the store line: the owner opens the store's own Edit Store window over
   * this one, and hands this window the store as saved. Without it, no link is drawn.
   */
  onEditStore?: () => void;
  /**
   * Opened for one person's replacement (Dashboard › Hiring's Notice period tab, 16 Oct 2026):
   * their employee code. With a store's row (`entry="store"`), the reason opens as "Replacing
   * someone", and that person opens chosen once the store's people are read - when they are
   * listed there and can be picked; otherwise nobody is chosen (never another person in their
   * place). Choosing a person chooses their own budget, as always. Everything stays changeable.
   */
  replacing?: string;
  /**
   * Opened by "Raise again" on an opening deleted in Recruitment: that link. Once this raise has
   * made an opening, it is no longer followed (the owner is told with `onChanged`, as for any
   * stop). Never in ask mode: there the result only says it stays followed.
   */
  replacingDeleted?: HiringLink;
}) {
  const fixedStore = (entry ?? (subject?.kind === 'store' ? 'store' : 'dashboard')) === 'store' && subject?.kind === 'store'
    ? subject
    : null;
  /**
   * Opened for a person who may only ask: the last step saves a hiring request for someone
   * else to confirm, and nothing goes to Recruitment. Fixed for the life of the window.
   */
  const asking = mode === 'ask';

  const [step, setStep] = useState<'form' | 'review' | 'result'>('form');

  // ── the form ──
  const [reason, setReason] = useState<HiringRaiseReason | ''>(fixedReason ?? (replacing && fixedStore ? 'replacement' : ''));
  // The dashboard's store: picked, or filled from the person replaced. Null: no store.
  const [pickedStore, setPickedStore] = useState<HiringStoreOption | null>(null);
  const [storeIsTheirs, setStoreIsTheirs] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [person, setPerson] = useState<HiringPersonOption | null>(null);
  const [budgets, setBudgets] = useState<HiringRaiseBudget[] | null>(null);
  const [budgetsError, setBudgetsError] = useState<string | null>(null);
  const [budgetReads, setBudgetReads] = useState(0);
  const [budgetId, setBudgetId] = useState('');
  const [storeCity, setStoreCity] = useState<PickedCity | null>(null);
  const [typedCity, setTypedCity] = useState<PickedCity | null>(null);
  const [typedAddress, setTypedAddress] = useState('');
  const [typedPincode, setTypedPincode] = useState('');
  // The map point of an address picked from Google's suggestions; none for a typed one.
  const [typedPoint, setTypedPoint] = useState<HiringMapPoint | null>(null);
  const [needs, setNeeds] = useState<RaiseFormNeeds>(NO_RAISE_NEEDS);
  const [additional, setAdditional] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(false);
  const [confirmNoRecord, setConfirmNoRecord] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── the review and the confirm ──
  const [reviewed, setReviewed] = useState<Reviewed | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // No answer came back: only the same request may be repeated.
  const [lost, setLost] = useState(false);
  // The server said it could not check just now, and that nothing was sent: its sentence
  // stands alone, and the button repeats the same request ("Ask again").
  const [couldNotCheck, setCouldNotCheck] = useState(false);
  // An answer was lost at some point in this review. From then on the form is not offered
  // again from this review, whatever a repeat is told: only an outcome ends it.
  const [locked, setLocked] = useState(false);
  const [blocking, setBlocking] = useState<HiringRaiseBlocking | null>(null);
  // The unlinked opening a refusal said is already there, under the budget it was reviewed for.
  const [alreadyThere, setAlreadyThere] = useState<{ budgetId: number; openingId: number } | null>(null);
  // The sentence of a refusal that said the budget has no free position: the Manage Budgets
  // link is beside it while it is the one on show.
  const [noFreePosition, setNoFreePosition] = useState<string | null>(null);

  // ── the result ──
  const [result, setResult] = useState<HiringRaiseResult | null>(null);
  // The deleted opening this raise replaces: being let go of, let go of, or still followed.
  const [deletedLetGo, setDeletedLetGo] = useState<'stopping' | 'stopped' | 'failed' | null>(null);
  const letGoAsked = useRef(false);
  // Ask mode: the hiring request that was saved, and the server's sentence for it.
  const [asked, setAsked] = useState<HiringRequestSent | null>(null);

  const alive = useRef(true);
  // One confirm at a time, however fast the clicks come.
  const sending = useRef(false);
  const owner = useRef({ onChanged, onAsked, onOutOfDate, onHiringGone, onRaiseGone });
  owner.current = { onChanged, onAsked, onOutOfDate, onHiringGone, onRaiseGone };

  // Raised again for a deleted opening, and this raise made an opening: the deleted one is no
  // longer followed, in the same step, once. Its own "Stop following" call: nothing is sent to
  // Recruitment. Someone else stopping it first is the same end.
  const outcomeNow = result ? raiseOutcomeKind(result.outcome) : null;
  const madeOpening = outcomeNow === 'created' || outcomeNow === 'already_linked';
  const deletedId = replacingDeleted?.id ?? null;
  useEffect(() => {
    if (!deletedId || asking || !madeOpening || letGoAsked.current) return;
    letGoAsked.current = true;
    setDeletedLetGo('stopping');
    Promise.resolve()
      .then(() => hiringLinksAPI.release(projectId, deletedId))
      .then((res) => {
        const closed = res?.data?.data;
        // The owner is told whether or not this window is still open.
        if (closed) owner.current.onChanged(closed);
        else owner.current.onOutOfDate?.();
        if (alive.current) setDeletedLetGo('stopped');
      })
      .catch((err: unknown) => {
        if (isHiringOff(err)) {
          owner.current.onHiringGone();
          return;
        }
        const endedElsewhere = hiringCodeOf(err) === HIRING_CODES.linkNotLive;
        if (endedElsewhere) owner.current.onOutOfDate?.();
        if (alive.current) setDeletedLetGo(endedElsewhere ? 'stopped' : 'failed');
      });
  }, [deletedId, asking, madeOpening, projectId]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** A refusal that ends the window: hiring, or raising, was switched off. */
  const switchedOff = (err: unknown): boolean => {
    if (isHiringOff(err)) {
      owner.current.onHiringGone();
      return true;
    }
    if (isRaiseOff(err)) {
      owner.current.onRaiseGone();
      return true;
    }
    return false;
  };
  const refusedByPicker = useRef(switchedOff);
  refusedByPicker.current = switchedOff;
  const pickerRefused = (err: unknown) => refusedByPicker.current(err);

  // The store the opening is for: the row's, or the one picked (or filled from the person).
  const store: HiringStoreOption | null = fixedStore ? storeOfSubject(fixedStore) : pickedStore;
  const kind: 'store' | 'role' = store ? 'store' : 'role';
  // The store "Who" lists the people of (15 Oct 2026): the row's, or one picked by hand. A store
  // filled from the person picked ("Their store") does not narrow it: the person came first.
  const handPickedStore = !fixedStore && pickedStore && !storeIsTheirs ? pickedStore : null;
  const whoStoreId = fixedStore?.storeId ?? handPickedStore?.storeId ?? null;

  // Every active budget of the project, read once a reason is chosen, and again when the
  // store comes or goes (a store's Store-level budgets come first).
  const hasReason = !!reason;
  useEffect(() => {
    if (!hasReason) {
      setBudgets(null);
      setBudgetsError(null);
      return undefined;
    }
    let cancelled = false;
    setBudgets(null);
    setBudgetsError(null);
    const why = (reason || undefined) as HiringRaiseReason | undefined;
    Promise.resolve()
      .then(() => (asking
        ? hiringLinksAPI.requestBudgets(projectId, kind, why)
        : hiringLinksAPI.raiseBudgets(projectId, kind, why)))
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data?.budgets ?? [];
        setBudgets(list);
        // A budget no longer offered, or full, is not kept. The only one that can be picked is
        // chosen - except for a replacement: there it is the person's own (chosen below).
        const usable = list.filter((b) => !(b.full ?? budgetIsFull(b)));
        setBudgetId((prev) => (usable.some((b) => String(b.budgetId) === prev)
          ? prev
          : usable.length === 1 && reasonNow.current !== 'replacement' ? String(usable[0].budgetId) : ''));
      })
      .catch((err: unknown) => {
        if (cancelled || refusedByPicker.current(err)) return;
        setBudgetsError(raiseErrorText(err, RAISE_WORDS.budgetsFailed));
      });
    return () => {
      cancelled = true;
    };
    // The reason is read when the list is asked for; a change of reason alone does not read it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, kind, hasReason, budgetReads, asking]);
  const reasonNow = useRef(reason);
  reasonNow.current = reason;

  // A replacement: the person's own budget is chosen for them when it is offered and not
  // full (once per person picked); otherwise nothing is chosen. They can choose another.
  const budgetFollowed = useRef('');
  useEffect(() => {
    if (reason !== 'replacement' || !person || !budgets) return;
    if (budgetFollowed.current === person.empCode) return;
    budgetFollowed.current = person.empCode;
    const own = person.budgetId != null ? budgets.find((b) => b.budgetId === person.budgetId) : undefined;
    setBudgetId(own && !(own.full ?? budgetIsFull(own)) ? String(own.budgetId) : '');
  }, [reason, person, budgets]);

  // The Recruitment team, once a budget is chosen. A person who asks picks none.
  const team = useHiringTeam({
    projectId,
    budgetId: !asking && budgetId ? Number(budgetId) : null,
    enabled: !asking,
    onRefused: (err) => refusedByPicker.current(err),
  });

  const chooseReason = (next: HiringRaiseReason) => {
    setReason(next);
    setFormError(null);
    if (next !== 'replacement') {
      setPerson(null);
      if (storeIsTheirs) {
        setPickedStore(null);
        setStoreIsTheirs(false);
      }
    }
    budgetFollowed.current = '';
  };

  /**
   * From the dashboard, a person's store is looked up in the store search, for its address. A
   * store picked by hand stays: "Who" listed that store's people only, and the store is the
   * person's choice, not the picked person's.
   */
  const fillStoreFrom = (picked: HiringPersonOption) => {
    if (fixedStore || handPickedStore) return;
    if (!picked.store) {
      setPickedStore(null);
      setStoreIsTheirs(false);
      return;
    }
    const known = storeOfPerson(picked.store);
    setPickedStore(known);
    setStoreIsTheirs(true);
    const code = picked.store.storeCode || picked.store.storeName;
    if (!code) return;
    Promise.resolve()
      .then(() => (asking ? hiringLinksAPI.requestStores(projectId, code) : hiringLinksAPI.raiseStores(projectId, code)))
      .then((res) => {
        const found = res?.data?.data?.stores?.find((s) => s.storeId === known.storeId);
        if (!found || !alive.current) return;
        // Only if the person has not changed the store meanwhile.
        setPickedStore((now) => (now?.storeId === found.storeId ? found : now));
      })
      .catch((err: unknown) => {
        refusedByPicker.current(err);
      });
  };

  const pickPerson = (picked: HiringPersonOption | null) => {
    setPerson(picked);
    setConfirmNoRecord(false);
    setFormError(null);
    if (picked) fillStoreFrom(picked);
  };

  // The Leaving list of a store's row: the store's one leaver opens chosen, as before - or,
  // opened for one person's replacement, that person (and nobody else) when they can be picked.
  const leavingRead = (answer: HiringPeopleSearch) => {
    if (!fixedStore || person) return;
    if (replacing) {
      const wanted = replacing.trim().toUpperCase();
      const them = answer.people.find((p) => p.empCode.trim().toUpperCase() === wanted && p.pickable !== false);
      if (them) setPerson(them);
      return;
    }
    const pre = answer.preselected ? answer.people.find((p) => p.empCode === answer.preselected && p.pickable !== false) : undefined;
    if (pre) setPerson(pre);
  };

  // An address picked from Google's suggestions: its text, its PIN code and map point, and its city when Recruitment's list has it.
  const addressPicked = (pick: AddressPick) => {
    setTypedAddress(pick.address);
    if (pick.pincode !== undefined) setTypedPincode(pick.pincode);
    if (pick.point !== undefined) setTypedPoint(pick.point);
    if (pick.city) setTypedCity(pick.city);
  };

  // A city or PIN changed by hand after a pick no longer matches the picked place: its map
  // point is dropped, and the opening is placed at the city's point, as for a typed address.
  // (A pick sets the city and the PIN itself, above, and keeps its point.)
  const cityChangedByHand = (next: PickedCity | null) => {
    if ((next?.cityId ?? null) !== (typedCity?.cityId ?? null)) setTypedPoint(null);
    setTypedCity(next);
  };
  const pincodeChangedByHand = (text: string) => {
    const next = text.replace(/\D/g, '');
    if (next !== typedPincode) setTypedPoint(null);
    setTypedPincode(next);
  };

  const replaces = reason === 'replacement' && person ? person.empCode : '';
  const typedProblem = !store ? typedAddressMissing({ cityId: typedCity?.cityId ?? null, address: typedAddress, pincode: typedPincode }) : null;
  const noRecordTick = needs.noRecordFor != null && needs.noRecordFor === replaces;
  // The budget and the person as they are on screen now.
  const budgetOnScreen = !!budgetId && !!budgets?.some((b) => String(b.budgetId) === budgetId && !(b.full ?? budgetIsFull(b)));
  const personOnScreen = reason !== 'replacement' || !!fixedReason || (!!person && person.pickable !== false);
  const teamMissing = !asking && budgetOnScreen ? teamMissingLine(team.draft) : null;
  const missing = raiseMissingLine({
    reason: !reason,
    person: reason === 'replacement' && !fixedReason && !personOnScreen,
    budget: !budgetOnScreen,
    address: typedProblem,
    team: teamMissing,
  });
  const canReview = !!reason && budgetOnScreen && personOnScreen && !typedProblem
    && (asking || team.ready) && !reviewing;
  const chosenBudget = budgets?.find((b) => String(b.budgetId) === budgetId) ?? null;

  /**
   * A refusal the form answers: its sentence, the tick or city picker it asks for, the team
   * it marks or the rounds it reads again, and - when it says an unlinked opening is already
   * there - that opening, to link instead. Whatever was reviewed is thrown away with its key.
   */
  const refusedOnForm = (err: unknown, sentence: string, reviewedBudgetId: number) => {
    setNeeds((prev) => raiseNeedsAfter(prev, err, { subjectKind: kind, replacesEmpCode: replaces }));
    const openingId = openingToLinkInstead(err);
    setAlreadyThere(openingId != null ? { budgetId: reviewedBudgetId, openingId } : null);
    setNoFreePosition(isNoFreePosition(err) ? sentence : null);
    const code = hiringCodeOf(err);
    if (code === TEAM_CODES.personNotAllowed) team.markNotAllowed(teamNotAllowedOf(err));
    // The budget's rounds changed in Recruitment: the team is read, and filled, again.
    if (code === TEAM_CODES.roundsChanged) team.reload();
    setFormError(sentence);
    setReviewed(null);
    setStep('form');
  };

  const review = async () => {
    if (!canReview) return;
    const body = buildRaiseBody({
      subject: store ? { kind: 'store', storeId: store.storeId } : { kind: 'role' },
      budgetId: Number(budgetId),
      reason: reason as HiringRaiseReason,
      replacesEmpCode: replaces,
      storeCityId: needs.cityPicker ? storeCity?.cityId ?? null : null,
      roleAddress: { source: 'typed', cityId: typedCity?.cityId ?? null, address: typedAddress, pincode: typedPincode, point: typedPoint },
      additional: needs.additional && additional,
      confirmStatus: needs.staffingStatus != null && confirmStatus,
      confirmNoRecord: noRecordTick && confirmNoRecord,
      // The person who asks picks no team: the person who confirms does.
      team: asking ? null : team.team,
    });
    setReviewing(true);
    setFormError(null);
    setConfirmError(null);
    setAlreadyThere(null);
    try {
      const res = asking
        ? await hiringLinksAPI.previewRequest(projectId, body)
        : await hiringLinksAPI.previewRaise(projectId, body);
      const preview = res?.data?.data;
      if (!alive.current) return;
      // A review with nothing to read is not a review: nothing may be confirmed on it.
      if (!preview || typeof preview.previewHash !== 'string' || !Array.isArray(preview.sentences) || preview.sentences.length === 0) {
        setFormError(RAISE_WORDS.reviewUnreadable);
        setStep('form');
        return;
      }
      // A new review, so a new key: this one belongs to this review alone.
      setReviewed({ preview, body, requestKey: makeRaiseRequestKey() });
      setLost(false);
      setLocked(false);
      setCouldNotCheck(false);
      setBlocking(null);
      setStep('review');
    } catch (err) {
      if (switchedOff(err)) return;
      // Something the page does not show yet is in the way: its list is read again.
      if (listIsBehindAfter(err)) owner.current.onOutOfDate?.();
      if (!alive.current) return;
      refusedOnForm(err, raiseErrorText(err, RAISE_WORDS.reviewFailed), body.budgetId);
    } finally {
      if (alive.current) setReviewing(false);
    }
  };

  /**
   * Ask mode's last step, "Send for confirmation": the form is saved as a hiring request.
   * NOTHING is sent to Recruitment. The same key goes with every repeat, so the same send
   * twice saves one request.
   */
  const sendForConfirmation = async () => {
    if (!reviewed || sending.current) return;
    sending.current = true;
    setConfirming(true);
    setConfirmError(null);
    setBlocking(null);
    try {
      const res = await hiringLinksAPI.sendRequest(projectId, { ...reviewed.body, requestKey: reviewed.requestKey });
      const answered = res?.data?.data;
      if (!answered || !answered.request) {
        // An answer that cannot be read is no answer: only the same send may be repeated.
        owner.current.onOutOfDate?.();
        if (alive.current) {
          setLost(true);
          setLocked(true);
        }
        return;
      }
      // The owner is told whether or not this window is still open. An owner that opened
      // this window to RAISE may not take a hiring request: its lists are read again.
      if (owner.current.onAsked) owner.current.onAsked(answered.request);
      else owner.current.onOutOfDate?.();
      if (!alive.current) return;
      setLost(false);
      setAsked(answered);
      setStep('result');
    } catch (err) {
      if (switchedOff(err)) return;
      const status = hiringStatusOf(err);
      const refused = status !== undefined && status < 500 && !!hiringCodeOf(err);
      if (!refused && nothingWasSentBy(err)) {
        // A server error that says by its code that nothing was done (Recruitment did not
        // answer a check): its sentence stands alone, and the same send can be asked again.
        if (!alive.current) return;
        setLost(false);
        setCouldNotCheck(true);
        setConfirmError(raiseErrorText(err, REQUEST_WORDS.askRefusedPlain));
        return;
      }
      if (!refused) {
        // No answer, or an error of the server's own: the hiring request may have been saved.
        owner.current.onOutOfDate?.();
        if (!alive.current) return;
        setLost(true);
        setLocked(true);
        setConfirmError(hiringCodeOf(err) ? raiseErrorText(err, '') || null : null);
        return;
      }
      // A definite "no": nothing was saved by this send.
      if (listIsBehindAfter(err)) owner.current.onOutOfDate?.();
      if (!alive.current) return;
      setLost(false);
      setCouldNotCheck(false);
      const sentence = raiseErrorText(err, REQUEST_WORDS.askRefusedPlain);
      const next = raiseRefusalStep(err);
      if (!locked && (next === 'review_again' || next === 'form')) {
        // What was reviewed is not what would be saved: back to the form, with the server's
        // sentence. The review and its key are gone; "Review" makes a new one of each.
        refusedOnForm(err, sentence, reviewed.body.budgetId);
      } else {
        // The sentence is shown here, and the only thing to press repeats this same send with this same key.
        setConfirmError(sentence);
      }
    } finally {
      sending.current = false;
      if (alive.current) setConfirming(false);
    }
  };

  const confirm = async () => {
    if (!reviewed || sending.current) return;
    // An ask-mode window never raises: its last step saves a hiring request.
    if (asking) {
      await sendForConfirmation();
      return;
    }
    sending.current = true;
    setConfirming(true);
    setConfirmError(null);
    setBlocking(null);
    try {
      const res = await hiringLinksAPI.raise(projectId, {
        ...reviewed.body,
        requestKey: reviewed.requestKey,
        previewHash: reviewed.preview.previewHash,
      });
      const answered = res?.data?.data;
      if (!answered || !answered.link) {
        // An answer that cannot be read is no answer: only the same request may be repeated.
        owner.current.onOutOfDate?.();
        if (alive.current) {
          setLost(true);
          setLocked(true);
        }
        return;
      }
      // The owner is told whether or not this window is still open.
      owner.current.onChanged(answered.link);
      if (!alive.current) return;
      setLost(false);
      setResult(answered);
      setStep('result');
    } catch (err) {
      if (switchedOff(err)) return;
      const status = hiringStatusOf(err);
      const refused = status !== undefined && status < 500 && !!hiringCodeOf(err);
      if (!refused && nothingWasSentBy(err)) {
        // A server error that says by its code that NOTHING WAS SENT: Recruitment did not
        // answer a check, which is made before anything is claimed. Its sentence stands
        // alone, and the only thing to press repeats this same request, with this same key.
        if (!alive.current) return;
        setLost(false);
        setCouldNotCheck(true);
        setConfirmError(raiseErrorText(err, RAISE_WORDS.refusedPlain));
        setBlocking(null);
        return;
      }
      if (!refused) {
        // No answer, or an error of the server's own: the request may have gone through.
        owner.current.onOutOfDate?.();
        if (!alive.current) return;
        setLost(true);
        setLocked(true);
        setConfirmError(hiringCodeOf(err) ? raiseErrorText(err, '') || null : null);
        return;
      }
      // A definite "no" from the checks: nothing was sent by this request.
      if (listIsBehindAfter(err)) owner.current.onOutOfDate?.();
      if (!alive.current) return;
      setLost(false);
      setCouldNotCheck(false);
      const sentence = raiseErrorText(err, RAISE_WORDS.refusedPlain);
      setNoFreePosition(isNoFreePosition(err) ? sentence : null);
      const next = raiseRefusalStep(err);
      if (locked) {
        // An earlier answer was lost: the refusal is shown, and the form stays out of reach.
        setConfirmError(sentence);
        setBlocking(blockingRequestOf(err));
      } else if (next === 'review_again' || next === 'form') {
        // What was reviewed is not what would be sent: back to the form, with the server's sentence.
        refusedOnForm(err, sentence, reviewed.body.budgetId);
      } else {
        // Nothing on the form answers it. The sentence is shown here, and "Yes, raise
        // hiring" repeats this same request with this same key.
        setConfirmError(sentence);
        setBlocking(blockingRequestOf(err));
      }
    } finally {
      sending.current = false;
      if (alive.current) setConfirming(false);
    }
  };

  const close = () => {
    // A request that is out, or whose answer was lost, may be on the owner's list by now.
    if (confirming || locked) onOutOfDate?.();
    onClose();
  };

  const took = (next: HiringRaiseResult) => {
    // The owner re-reads its list only when the request really moved on.
    if (!result || raiseLinkMoved(result.link, next.link)) owner.current.onChanged(next.link);
    if (alive.current) setResult(next);
  };

  const raiseAgain = () => {
    setResult(null);
    setReviewed(null);
    setConfirmError(null);
    setFormError(null);
    setLost(false);
    setLocked(false);
    setCouldNotCheck(false);
    setBlocking(null);
    setAlreadyThere(null);
    setStep('form');
  };

  /** "Link it instead": the owner closes this window and opens Link an opening on that opening. */
  const linkInstead = (pick: { budgetId: number; openingId: number }) => {
    onLinkInstead?.({
      ...pick,
      ...(store ? { store: { storeId: store.storeId, storeName: store.storeName, storeCode: store.storeCode } } : {}),
    });
  };

  const title = asking ? RAISE_FORM.askTitle : RAISE_FORM.title;
  // "Link it instead" follows an opening by hand, which a person who may only ask cannot do here.
  const offerLinkInstead = !asking && !!onLinkInstead;
  // A request already being raised for this store: it is shown, and no second one is offered.
  const inFlight = step !== 'result' ? links.find((l) => l.state === 'sending') ?? null : null;
  const closeButton = (
    <button type="button" className={SECONDARY} onClick={close}>{RAISE_WORDS.close}</button>
  );

  // ── ask mode: the hiring request was saved ──
  if (step === 'result' && asked) {
    return (
      <Modal isOpen onClose={close} title={title} size="lg" footer={closeButton}>
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">{REQUEST_WORDS.sentHeading}</h4>
          <p role="status" className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
            {localiseRaiseMessage(asked.message)}
          </p>
          {/* A hiring request lets go of nothing: the deleted opening is still followed. */}
          {replacingDeleted && <p className="text-[13px] text-gray-700">{DELETED_WORDS.askLeft(replacingDeleted.openingId)}</p>}
        </div>
      </Modal>
    );
  }

  // ── the result ──
  if (step === 'result' && result) {
    const outcome = raiseOutcomeKind(result.outcome);
    const tone = outcome === 'created'
      ? (result.link.attention?.includes('created_incomplete') || result.link.attention?.includes('not_listed')
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-green-200 bg-green-50 text-green-900')
      : 'border-gray-200 bg-gray-50 text-gray-800';
    return (
      <Modal
        isOpen
        onClose={close}
        title={title}
        size="lg"
        footer={(
          <>
            {closeButton}
            {/* Only where nothing was created. A request Recruitment did not confirm never offers it. */}
            {outcome === 'nothing' && (
              <button type="button" className={PRIMARY} onClick={raiseAgain}>{RAISE_WORDS.raiseAgain}</button>
            )}
          </>
        )}
      >
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">{raiseOutcomeHeading(result.outcome)}</h4>
          {outcome === 'waiting' ? (
            <RaiseRequestStatus
              projectId={projectId}
              link={result.link}
              canManage
              initial={result}
              onResult={took}
              onHiringGone={onHiringGone}
            />
          ) : (
            <p role="status" className={`rounded-lg border px-3 py-2 text-sm ${tone}`}>{localiseRaiseMessage(result.message)}</p>
          )}
          {/* The deleted opening this raise replaces: let go of in the same step, or still followed. */}
          {replacingDeleted && outcome === 'waiting' && deletedLetGo == null && (
            <p className="text-[13px] text-gray-500" title={DELETED_WORDS.waitingHover(replacingDeleted.openingId)}>
              {DELETED_WORDS.waiting(replacingDeleted.openingId)}
            </p>
          )}
          {replacingDeleted && deletedLetGo === 'stopping' && (
            <p className="inline-flex items-center gap-2 text-[13px] text-gray-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {DELETED_WORDS.stopping(replacingDeleted.openingId)}
            </p>
          )}
          {replacingDeleted && deletedLetGo === 'stopped' && (
            <p className="text-[13px] text-gray-700">{DELETED_WORDS.stopped(replacingDeleted.openingId)}</p>
          )}
          {replacingDeleted && deletedLetGo === 'failed' && (
            <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
              {DELETED_WORDS.stopFailed(replacingDeleted.openingId)}
            </p>
          )}
        </div>
      </Modal>
    );
  }

  // ── a request is already out for this store ──
  if (inFlight) {
    return (
      <Modal isOpen onClose={close} title={title} size="lg" footer={closeButton}>
        <div className="space-y-3">
          <p className="text-sm text-gray-700">{RAISE_WORDS.alreadyRaising(kind)}</p>
          <RaiseRequestStatus
            projectId={projectId}
            link={inFlight}
            // A person who may only ask sees where it stands, and nothing to press.
            canManage={!asking}
            onResult={(next) => {
              if (raiseLinkMoved(inFlight, next.link)) owner.current.onChanged(next.link);
              // Once it has ended, how it ended is shown here; the form is not put in its place.
              if (alive.current && raiseOutcomeKind(next.outcome) !== 'waiting') {
                setResult(next);
                setStep('result');
              }
            }}
            onHiringGone={onHiringGone}
          />
        </div>
      </Modal>
    );
  }

  // ── the review ──
  if (step === 'review' && reviewed) {
    const { preview } = reviewed;
    const opener = blocking && openRequest ? openRequest(blocking.linkId) : null;
    const sendingWord = asking ? REQUEST_WORDS.sending : RAISE_WORDS.sending;
    // An unlinked opening already there: it can be followed in place of raising one more.
    const unlinked = preview.existingUnlinked?.find((opening) => typeof opening?.openingId === 'number') ?? null;
    const footnote = preview.confirm?.footnote || (asking ? REQUEST_WORDS.askReviewFootnote : RAISE_WORDS.footnote);
    const reviewedStore = store;
    return (
      <Modal
        isOpen
        onClose={close}
        title={title}
        size="lg"
        footer={(
          <div className="flex w-full flex-col gap-2">
            {lost && (
              <div role="alert" className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {confirmError && <p>{confirmError}</p>}
                <p className="font-medium">{asking ? REQUEST_WORDS.lostAnswer : RAISE_WORDS.lostAnswer}</p>
                <p>{asking ? REQUEST_WORDS.lostAnswerNext : RAISE_WORDS.lostAnswerNext}</p>
              </div>
            )}
            {!lost && confirmError && <FormErrorBanner message={confirmError} />}
            {!lost && confirmError && confirmError === noFreePosition && <ManageBudgetsLink projectId={projectId} className="self-start" />}
            {!lost && locked && !asking && <p className="text-xs text-amber-900">{RAISE_WORDS.lockedNote}</p>}
            {confirming && !asking && <p className="text-xs text-gray-600">{RAISE_WORDS.sendingNote}</p>}
            <div className="flex flex-wrap items-center justify-end gap-2.5">
              <span className="w-full text-[12.5px] text-gray-500 sm:w-auto sm:min-w-0 sm:flex-1">{footnote}</span>
              {opener && (
                <button type="button" className={SECONDARY} onClick={opener}>{openBlockingWords(blocking)}</button>
              )}
              {lost || locked || couldNotCheck ? (
                <>
                  {closeButton}
                  {/* The same request, with the same key. Never a new one. */}
                  <button type="button" className={PRIMARY} onClick={() => void confirm()} disabled={confirming}>
                    {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
                    {confirming ? sendingWord : RAISE_WORDS.askAgain}
                  </button>
                </>
              ) : (
                <>
                  {unlinked && offerLinkInstead && (
                    <button
                      type="button"
                      className={SECONDARY}
                      onClick={() => linkInstead({ budgetId: reviewed.body.budgetId, openingId: unlinked.openingId })}
                      disabled={confirming}
                    >
                      {RAISE_WORDS.linkInstead}
                    </button>
                  )}
                  <button type="button" className={SECONDARY} onClick={() => setStep('form')} disabled={confirming || reviewing}>
                    {REVIEW_WORDS.back}
                  </button>
                  <button type="button" className={PRIMARY} onClick={() => void confirm()} disabled={confirming}>
                    {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
                    {confirming ? sendingWord : preview.confirm?.yes || (asking ? REQUEST_WORDS.askYes : RAISE_WORDS.yes)}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      >
        <RaiseReview
          preview={preview}
          asking={asking}
          facts={{
            openingFor: openingStoreWords(reviewedStore),
            replaces: reason === 'replacement' && person
              ? reviewReplacesWords({ empCode: person.empCode, name: preview.replacement?.name ?? person.name, tag: personTagOf(person) })
              : null,
            budgetName: chosenBudget?.budgetName ?? '',
            budgetFree: chosenBudget?.free ?? null,
            address: reviewedStore
              ? [reviewedStore.address, needs.cityPicker ? storeCity?.label : reviewedStore.city, reviewedStore.pincode]
                .map((part) => String(part ?? '').trim()).filter(Boolean).join(' · ') || null
              : [typedAddress.trim(), typedCity?.label, typedPincode.trim()].filter(Boolean).join(' · ') || null,
            subjectKind: kind,
          }}
        />
      </Modal>
    );
  }

  // ── the form ──
  const busy = reviewing;
  const storeLineAddress = fixedStore
    ? [fixedStore.address, fixedStore.city, fixedStore.pincode].map((part) => String(part ?? '').trim()).filter(Boolean).join(' · ')
    : '';
  const storeIncomplete = fixedStore ? !fixedStore.hasMapLocation || !/^[1-9]\d{5}$/.test(String(fixedStore.pincode ?? '').trim()) : false;
  const reasonRow = (
    <FormRow label={RAISE_FORM.why} labelId="raise-hiring-why">
      {fixedReason ? (
        <p className="pt-2 text-sm font-semibold text-gray-900">{raiseReasonLabel(fixedReason)}</p>
      ) : (
        <Segmented
          labelledBy="raise-hiring-why"
          options={RAISE_SEGMENTS.map((option) => ({ ...option, hover: raiseReasonHover(option.value, fixedStore ? 'store' : 'role') }))}
          value={reason}
          onChange={chooseReason}
          disabled={busy}
        />
      )}
    </FormRow>
  );
  const whoRow = reason === 'replacement' && !fixedReason ? (
    <FormRow label={RAISE_FORM.who}>
      <PersonPicker
        projectId={projectId}
        asking={asking}
        storeId={whoStoreId}
        value={person}
        onChange={pickPerson}
        onLeaving={leavingRead}
        onRefused={pickerRefused}
        disabled={busy}
      />
    </FormRow>
  ) : null;
  const storeRow = !fixedStore ? (
    <FormRow label={RAISE_FORM.store} optional={RAISE_FORM.optional}>
      <StorePicker
        projectId={projectId}
        mode={asking ? 'ask' : 'raise'}
        value={pickedStore}
        onChange={(next) => {
          setPickedStore(next);
          setStoreIsTheirs(false);
          setFormError(null);
          // A store chosen by hand narrows "Who" to its people: a person picked before who is
          // not of that store is dropped (the server would refuse them, REPLACED_NOT_AT_STORE).
          if (next && person && person.store?.storeId !== next.storeId) {
            setPerson(null);
            setConfirmNoRecord(false);
          }
        }}
        onRefused={pickerRefused}
        tag={storeIsTheirs && pickedStore ? { text: RAISE_FORM.theirStore, tone: 'amber' } : null}
        disabled={busy}
      />
      {pickedStore && storeAddressLine(pickedStore) && (
        <p className="mt-1.5 flex items-center gap-1.5 truncate text-[13px] text-gray-600" title="From the store record.">
          <Lock className="h-3.5 w-3.5 flex-none text-gray-400" aria-hidden="true" />
          {storeAddressLine(pickedStore)}
        </p>
      )}
    </FormRow>
  ) : null;
  const addressRows = !store ? (
    <>
      {/* The city's point is used only for an address that was typed, or whose city or PIN was changed by hand after a pick. */}
      <FormRow label={RAISE_FORM.cityPin} info={typedPoint ? undefined : RAISE_FORM.cityInfo}>
        <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
          <CitySelect projectId={projectId} asking={asking} value={typedCity} onChange={cityChangedByHand} onRefused={pickerRefused} disabled={busy} />
          <input
            type="text"
            inputMode="numeric"
            aria-label={RAISE_FORM.pin}
            placeholder={RAISE_FORM.pin}
            value={typedPincode}
            maxLength={6}
            onChange={(e) => pincodeChangedByHand(e.target.value)}
            disabled={busy}
            className={INPUT}
          />
        </div>
      </FormRow>
      <FormRow label={RAISE_FORM.address} htmlFor="raise-hiring-address">
        <HiringAddressInput
          id="raise-hiring-address"
          projectId={projectId}
          asking={asking}
          value={typedAddress}
          city={typedCity}
          onType={(text) => {
            setTypedAddress(text);
            setTypedPoint(null);
          }}
          onPick={addressPicked}
          disabled={busy}
          className={INPUT}
        />
      </FormRow>
    </>
  ) : null;
  const budgetRow = (
    <FormRow label={RAISE_FORM.budget}>
      {reason ? (
        <BudgetSelect
          budgets={budgets}
          error={budgetsError}
          onRetry={() => setBudgetReads((n) => n + 1)}
          value={budgetId}
          onChange={(value) => {
            setBudgetId(value);
            setFormError(null);
          }}
          ownBudgetId={reason === 'replacement' ? person?.budgetId ?? null : null}
          withStore={!!store}
          disabled={busy}
        />
      ) : (
        // The closed control, greyed until a reason is chosen: the foot already says what is missing.
        <HiringSelect
          label={RAISE_FORM.budget}
          value=""
          onChange={() => undefined}
          options={[]}
          placeholder={RAISE_FORM.budgetPlaceholder}
          searchPlaceholder={RAISE_FORM.budgetSearch}
          disabled
        />
      )}
    </FormRow>
  );

  return (
    <Modal
      isOpen
      onClose={close}
      title={title}
      size="lg"
      footer={(
        <div className="flex w-full flex-col gap-2">
          {/* In the footer, not the scrolling body: a refusal must be seen wherever the form is scrolled to. */}
          {formError && <FormErrorBanner message={formError} />}
          {formError && formError === noFreePosition && <ManageBudgetsLink projectId={projectId} className="self-start" />}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <span className="min-w-0 flex-1 text-[12.5px] text-gray-500">{missing}</span>
            {/* The refusal above said an unlinked opening is already there. */}
            {alreadyThere && offerLinkInstead && (
              <button type="button" className={SECONDARY} onClick={() => linkInstead(alreadyThere)} disabled={busy}>
                {RAISE_WORDS.linkInstead}
              </button>
            )}
            <button type="button" className={SECONDARY} onClick={close} disabled={busy}>{RAISE_WORDS.cancel}</button>
            <button type="button" className={PRIMARY} onClick={() => void review()} disabled={!canReview}>
              {reviewing && <Loader2 className="h-4 w-4 animate-spin" />}
              {reviewing ? RAISE_WORDS.reviewing : RAISE_WORDS.review}
            </button>
          </div>
        </div>
      )}
    >
      <div>
        {fixedStore && (
          <div className="mb-1 border-b border-gray-100 pb-3">
            <div className="flex items-center justify-between gap-3 text-sm">
              <p className="min-w-0 truncate">
                <span className="font-semibold text-gray-900">{openingStoreWords(fixedStore)}</span>
                {fixedStore.city && <span className="text-gray-500">{` · ${fixedStore.city}`}</span>}
              </p>
              <span className="flex flex-none items-center gap-3">
                <button
                  type="button"
                  className={LINK}
                  title={storeLineAddress || undefined}
                  aria-expanded={showAddress}
                  onClick={() => setShowAddress((was) => !was)}
                >
                  {showAddress ? RAISE_FORM.hideAddress : RAISE_FORM.showAddress}
                </button>
                {onEditStore && (
                  <button type="button" className={LINK} title={RAISE_WORDS.editStoreHover} onClick={onEditStore} disabled={busy}>
                    {RAISE_WORDS.editStore}
                  </button>
                )}
              </span>
            </div>
            {showAddress && <p className="mt-1.5 text-[13px] text-gray-600">{storeLineAddress || RAISE_WORDS.notSet}</p>}
            {storeIncomplete && (
              <p className="mt-1.5 text-[13px] text-amber-800">{onEditStore ? RAISE_FORM.noMapLine : RAISE_FORM.noMapLineNoEdit}</p>
            )}
          </div>
        )}

        {reasonRow}
        {whoRow}
        {storeRow}
        {addressRows}
        {budgetRow}

        {store && needs.cityPicker && (
          <FormRow label={RAISE_FORM.storeCity}>
            <CitySelect
              projectId={projectId}
              asking={asking}
              label={RAISE_FORM.storeCity}
              value={storeCity}
              onChange={(city) => {
                setStoreCity(city);
                setFormError(null);
              }}
              onRefused={pickerRefused}
              disabled={busy}
            />
          </FormRow>
        )}

        {(needs.additional || needs.staffingStatus != null || noRecordTick) && (
          <div className="my-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            {needs.additional && (
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" className="mt-0.5" checked={additional} disabled={busy} onChange={(e) => setAdditional(e.target.checked)} />
                {RAISE_TICKS.additional(kind)}
              </label>
            )}
            {needs.staffingStatus != null && (
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" className="mt-0.5" checked={confirmStatus} disabled={busy} onChange={(e) => setConfirmStatus(e.target.checked)} />
                {RAISE_TICKS.status(needs.staffingStatus)}
              </label>
            )}
            {noRecordTick && (
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" className="mt-0.5" checked={confirmNoRecord} disabled={busy} onChange={(e) => setConfirmNoRecord(e.target.checked)} />
                {RAISE_TICKS.noRecord(replaces)}
              </label>
            )}
          </div>
        )}

        {/* The Recruitment team: picked here when raising, by the person who confirms when asking. */}
        {asking ? (
          <p className="mt-2 border-t border-gray-200 pt-3 text-[13px] text-gray-600">{RAISE_FORM.askTeam}</p>
        ) : budgetOnScreen ? (
          <HiringTeamSection team={team} busy={busy} />
        ) : null}
      </div>
    </Modal>
  );
}

export default RaiseHiringModal;
